// Central site configuration and content for MySiteCare

export const SITE = {
  name: "MySiteCare",
  legalName: "MySiteCare Website Application Services",
  tagline: "Website responsibility services for Philippine and international businesses",
  email: "info@mysitecare.eu",
  phone: "+63 931 825 9615",
  phoneHref: "+639318259615",
  website: "https://mysitecare.eu",
  serviceArea: "Philippines, Europe, and other international markets",
  year: 2026,
};

// ---------- Region system (International / EU vs Philippines) ----------
export const REGIONS = {
  eu: {
    id: "eu",
    base: "",
    label: "International / Europe",
    short: "INTL",
    currency: "EUR",
    website: "https://mysitecare.eu",
    source: "international",
  },
  ph: {
    id: "ph",
    base: "/ph",
    label: "Philippines",
    short: "PH",
    currency: "PHP",
    website: "https://mysitecare.eu/ph",
    source: "philippines",
  },
};

export function regionFromPath(pathname) {
  return pathname === "/ph" || pathname.startsWith("/ph/") ? "ph" : "eu";
}

// Build a region-aware path. rlink("ph","/plans") => "/ph/plans"; rlink("eu","/") => "/"
export function rlink(region, path) {
  const base = REGIONS[region]?.base || "";
  if (path === "/") return base || "/";
  return `${base}${path}`;
}

// Peso vs Euro pricing keyed by plan id.
//
// §5-6 of the standalone-handover/pricing task: Website Ownership and Assisted Setup &
// Launch are two SEPARATE one-time products, not one bundled price. Ownership alone
// (€600 / ₱42,000) is a complete, undegraded standalone-handover package the customer
// or their own developer deploys; Assisted Setup & Launch (+€300 / +₱21,000) is
// optional deployment/launch LABOR on top of it. The combined €900 / ₱63,000 figure is
// never hardcoded anywhere else — it is always derived via getCombinedOwnershipTotal()
// below, so the two prices can never silently drift apart from their sum.
export const PLAN_PRICING = {
  ownership: { eu: { price: "€600", period: "one-time" }, ph: { price: "₱42,000", period: "one-time" } },
  assisted_setup_launch: { eu: { price: "€300", period: "one-time" }, ph: { price: "₱21,000", period: "one-time" } },
  maintenance: { eu: { price: "€59", period: "/ month" }, ph: { price: "₱3,500", period: "/ month" } },
  responsibility: { eu: { price: "€129", period: "/ month" }, ph: { price: "₱7,500", period: "/ month" } },
  priority: { eu: { price: "€249", period: "/ month" }, ph: { price: "₱14,500", period: "/ month" } },
};

export function getPlanPrice(planId, region) {
  return (PLAN_PRICING[planId] && PLAN_PRICING[planId][region]) || PLAN_PRICING[planId].eu;
}

// Parses a "€600" / "₱42,000" display price back into a number, for the ONLY place a
// combined ownership+assisted total should ever be computed — never hand-typed.
function parsePriceNumber(display) {
  return Number(display.replace(/[^0-9.]/g, ""));
}

function formatPriceLike(template, amount) {
  const symbol = template.match(/^\D+/)?.[0] || "";
  return `${symbol}${amount.toLocaleString(template.includes("₱") ? "en-PH" : "en-US")}`;
}

export function getCombinedOwnershipTotal(region) {
  const ownership = getPlanPrice("ownership", region);
  const assisted = getPlanPrice("assisted_setup_launch", region);
  const total = parsePriceNumber(ownership.price) + parsePriceNumber(assisted.price);
  return { price: formatPriceLike(ownership.price, total), period: "one-time" };
}

// Philippines-focused audience for the /ph homepage
export const PH_AUDIENCE = [
  "Small businesses",
  "Rental businesses",
  "Local services",
  "Small offices",
  "Shops",
  "Clinics",
  "Agencies",
  "Local entrepreneurs",
];

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Plans", to: "/plans" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export const LEGAL_LINKS = [
  { label: "Payment Terms", to: "/clear-terms" },
  { label: "Refund & Cancellation Policy", to: "/refund-cancellation-policy" },
  { label: "What We Do", to: "/what-we-do" },
  { label: "What We Don't Do", to: "/what-we-dont" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Cookie Policy", to: "/cookie-policy" },
  { label: "Business Registration", to: "/business-information" },
];

export const STEPS = [
  {
    n: "01",
    title: "We build or take over the website",
    text: "We create the agreed website or take over an existing one where technically suitable.",
  },
  {
    n: "02",
    title: "You choose who stays responsible",
    text: "Own it and manage it yourself, choose basic maintenance, or delegate covered technical responsibility.",
  },
  {
    n: "03",
    title: "We follow that responsibility model",
    text: "If your plan assigns a technical issue to MySiteCare, you message us instead of troubleshooting it yourself.",
  },
];

export const WHAT_WE_HANDLE = [
  "Hosting and core updates, where included",
  "Backups, where included",
  "Uptime and system-issue monitoring, where included",
  "Plan-covered breakage fixes",
  "Limited small content edits, where included",
  "One technical point of contact for covered issues",
];

export const WHAT_WE_DONT = [
  "Traffic or leads",
  "SEO rankings",
  "Business results",
  "Marketing performance",
];

export const PLANS = [
  {
    id: "ownership",
    slug: "website-ownership",
    name: "Website Ownership",
    type: "One-Time",
    price: "€600",
    period: "one-time",
    note: "Applicable taxes shown where required",
    image: "/images/plan1.png",
    summary: "Get the complete standalone website package. You or your developer handle deployment.",
    subtitle: "A complete, ready-to-deploy website package handed over to you in full.",
    purpose:
      "Website Ownership is a one-time purchase of the complete, standalone website package: your actual website — independently deployable, no MySiteCare account or backend required — plus a full data export of every business system you've enabled, and setup documentation. Nothing is held back or degraded to encourage a bigger purchase — this is the complete deliverable. You or your own developer handle deployment, hosting, domain connection, and any third-party service configuration. Prefer not to do that yourself? Assisted Setup & Launch is available as an optional add-on, any time — before or after this purchase.",
    included: [
      "Complete standalone website package",
      "Full data export of every business system enabled for your site (appointments, customers, orders, inventory, and more)",
      "Full business data export",
      "Setup documentation and handover guide",
      "Agreed admin and file access",
      "Freedom to use a compatible hosting provider",
    ],
    excluded: ["Deployment, hosting setup, and domain/DNS configuration (see Assisted Setup & Launch)", "Ongoing hosting unless separately included", "Monitoring, maintenance, updates, and backups", "Technical support, edits, troubleshooting, or repairs"],
    positioning: "\u201CI own it. I manage it. If something breaks, it's on me.\u201D",
    best: "Technically confident owners, or owners with their own developer, who want full control.",
    footnote: "Future help requires an applicable care plan, Assisted Setup & Launch, or a separately approved quotation.",
    notes: [
      "Ownership and agreed access are transferred only after the required payment and handover are complete.",
      "Transfer covers agreed transferable deliverables; third-party licences and MySiteCare reusable systems keep their own terms.",
      "Deployment and technical setup are not included — see Assisted Setup & Launch if you'd like MySiteCare to handle that.",
      "After handover, all updates, security, backups, and fixes are your responsibility unless a care plan or Assisted Setup covers them.",
      "Any future work is quoted separately and performed only upon agreement.",
      "You can move to a responsibility plan later if you change your mind.",
    ],
    // §90 correction: Assisted Setup & Launch is a SUB-OPTION of Website Ownership, not
    // a separate browsable plan — it must never appear as its own card in the pricing
    // grid. It stays billable as its own line item (see PLAN_ADD_ONS below and the
    // backend billing catalog) purely for accounting clarity; the customer only ever
    // encounters it here, inside the Ownership plan's own card/detail page.
    addOns: [
      {
        id: "assisted_setup_launch",
        slug: "assisted-setup-launch",
        name: "Assisted Setup & Launch",
        summary: "Prefer not to handle the technical setup yourself? We deploy it and launch it with you.",
        included: [
          "Domain and DNS connection assistance",
          "Hosting, database, and environment configuration",
          "Configuration of supported third-party services you provide access to",
          "Final launch verification and smoke test",
          "Owner access confirmed working",
        ],
        footnote: "One-time deployment assistance, added to Website Ownership — not ongoing support. Can be added now or later without repurchasing ownership.",
      },
    ],
    highlight: false,
  },
  {
    id: "maintenance",
    slug: "maintenance-only",
    name: "Maintenance Only",
    type: "Subscription",
    price: "€59",
    period: "/ month",
    note: "Applicable taxes shown where required",
    image: "/images/plan2.png",
    summary: "We keep the basics running. You remain responsible.",
    subtitle: "Hosting, core updates and backups — you stay in control of problems.",
    purpose:
      "Maintenance Only keeps the technical basics running: hosting, core system updates, and regular backups. It is a low-cost foundation for owners who are comfortable handling issues themselves. Responsibility for fixing problems stays with you — this is not a peace-of-mind plan.",
    included: ["Hosting", "Core system updates", "Regular backups"],
    excluded: ["Monitoring", "Fixing issues", "Responsibility when something breaks"],
    positioning: "\u201CWe maintain. You manage problems.\u201D",
    best: "Price-sensitive clients who are comfortable troubleshooting issues themselves.",
    footnote: "This is not a peace-of-mind plan.",
    notes: [
      "We do not monitor the site or fix issues under this plan.",
      "If something breaks, resolution remains your responsibility.",
      "Hosting remains active only while payment is current.",
      "You can upgrade to the Responsibility Plan at any time.",
    ],
    highlight: false,
  },
  {
    id: "responsibility",
    slug: "responsibility-plan",
    name: "Responsibility Plan",
    type: "Subscription",
    price: "€129",
    period: "/ month",
    note: "Applicable taxes shown where required",
    image: "/images/plan3.png",
    summary: "For covered technical issues, you contact MySiteCare instead of diagnosing the problem yourself.",
    subtitle: "One website. One technical point of contact for covered issues.",
    purpose:
      "The Responsibility Plan assigns covered technical responsibility to MySiteCare. We host the website, keep it updated and backed up, monitor uptime and relevant system issues, and handle breakages covered by the accepted scope. If something within our agreed responsibility breaks, you contact us instead of diagnosing it first. The plan also includes a limited monthly allowance for minor edits.",
    included: [
      "Website included (build or takeover)",
      "Hosting, updates, and backups",
      "Uptime & issue monitoring",
      "Fixing breakages caused by updates or system issues",
      "One technical point of contact for covered issues",
      "Up to 30 minutes of small website edits per month",
    ],
    excluded: ["Marketing guarantees", "SEO rankings", "Paid ads", "Major redesigns"],
    positioning: "One website. One clear technical point of contact.",
    best: "Busy business owners who want peace of mind and zero technical stress.",
    footnote: "PH qualifying paid months may reduce the optional ownership-conversion price. EU/international subscription fees do not create ownership rights.",
    notes: [
      "Monthly payments do not create ownership rights — this is a responsibility relationship.",
      "Included edits are up to 30 minutes per calendar month and do not roll over.",
      "Major redesigns, new pages, and integrations are quoted separately.",
      "Services are active only while payment is current.",
    ],
    regional: {
      ph: {
        notes: [
          "Completed and fully paid Responsibility Plan months qualify for the PH ownership-conversion benefit.",
          "Ownership never transfers automatically; conversion requires a written request, verification, and handover.",
        ],
        extraSections: [
          {
            title: "Philippines ownership-conversion option",
            text: "The conversion base is the standalone Website Ownership price recorded in the accepted order when the qualifying subscription begins — Assisted Setup & Launch, if wanted, remains a separate optional charge and is never discounted by this schedule. Each of the first eight qualifying paid months adds 10 percentage points of discount. Months 8–23 remain at the maximum 80% discount. After 24 qualifying paid months, no additional MySiteCare Website Ownership purchase price is due, although outstanding balances, third-party costs, verification, and written handover still apply.",
          },
        ],
      },
      eu: {
        notes: ["EU / international subscription payments are service fees and do not purchase, finance, or progressively transfer website ownership."],
      },
    },
    highlight: true,
    badge: "Most chosen",
  },
  {
    id: "priority",
    slug: "priority-reputation-care",
    name: "Priority & Reputation Care",
    type: "Subscription",
    price: "€249",
    period: "/ month",
    note: "Applicable taxes shown where required",
    image: "/images/plan4.png",
    summary: "Your website is not allowed to embarrass you. For businesses where downtime, errors, or sloppiness damage trust.",
    subtitle: "Everything in the Responsibility Plan, with priority handling and proactive checks.",
    purpose:
      "Priority & Reputation Care is for businesses where downtime, errors, or sloppiness directly damage trust. It includes everything in the Responsibility Plan, plus priority response times, proactive checks, manual reviews, and extra attention to reliability and presentation. This plan is intentionally limited so responsibility doesn't get diluted.",
    included: [
      "Everything in the Responsibility Plan",
      "Priority response times",
      "Proactive checks and manual reviews",
      "Higher expectation handling",
      "Extra attention to reliability and presentation",
    ],
    excluded: ["Marketing services", "SEO or traffic guarantees", "Major redesigns"],
    positioning: "\u201CFailure is not acceptable.\u201D",
    best: "Established, reputation-sensitive businesses where trust is critical.",
    footnote: "This plan is intentionally limited.",
    notes: [
      "This plan is intentionally limited to a small number of clients.",
      "Priority response is about faster handling, not guaranteed turnaround unless agreed in writing.",
      "Monthly payments do not create ownership rights.",
      "Services are active only while payment is current.",
    ],
    regional: {
      ph: {
        notes: [
          "Completed and fully paid Priority plan months qualify under the same PH ownership-conversion schedule as the Responsibility Plan.",
          "Ownership never transfers automatically; conversion requires verification and written handover.",
        ],
      },
      eu: {
        notes: ["EU / international subscription payments remain service fees and do not create automatic ownership rights."],
      },
    },
    highlight: false,
  },
];

// Full plan-shaped detail for Website Ownership's add-on — kept SEPARATE from `PLANS`
// on purpose (see the `addOns` field on the ownership entry above): this is never
// rendered as its own browsable plan card. It exists here, shaped like a full plan
// object, only so commercialTemplates.js's proposal/agreement generator can still
// produce correct, separate legal text and pricing for it as its own billable line
// item — the commercial/accounting separation from Website Ownership is intentional
// and unchanged; only the customer-facing PRESENTATION as an independent product was
// wrong and is fixed by keeping it out of PLANS.
export const PLAN_ADD_ONS = [
  {
    id: "assisted_setup_launch",
    slug: "assisted-setup-launch",
    name: "Assisted Setup & Launch",
    type: "One-Time",
    price: "€300",
    period: "one-time",
    note: "Optional add-on to Website Ownership — one-time, not a subscription",
    summary: "Prefer not to handle the technical setup yourself? We deploy it and launch it with you.",
    subtitle: "MySiteCare deploys, configures, and launches your already-purchased website package.",
    purpose:
      "Assisted Setup & Launch is optional, one-time deployment labor performed on top of a Website Ownership purchase — it is not a different website and does not itself grant any additional ownership. We take your complete standalone website package and get it operational: connecting your domain, configuring hosting, the database, and the environment, connecting supported third-party services you've provided access to, and running final launch checks. It can be purchased together with Website Ownership or added on separately later — you never need to repurchase ownership to add it.",
    included: [
      "Domain and DNS connection assistance",
      "Hosting, database, and environment configuration",
      "Configuration of supported third-party services you provide access to",
      "Final launch verification and smoke test",
      "Owner access confirmed working",
    ],
    excluded: ["Website Ownership itself (purchased separately)", "Ongoing maintenance, monitoring, or support after launch", "Hosting, domain renewal, or third-party subscription fees", "Unlimited future changes"],
    positioning: "“We set it up and launch it. You take it from there.”",
    best: "Owners who want the complete package but prefer MySiteCare to handle the technical deployment.",
    footnote: "One-time deployment assistance — not ongoing support. A care plan covers what happens after launch.",
    notes: [
      "This is labor and convenience, not additional software functionality — the Website Ownership package is already complete without it.",
      "Does not include ongoing maintenance, hosting fees, domain renewal, or third-party subscription costs.",
      "Can be purchased at the same time as Website Ownership, or added later without repurchasing ownership.",
      "Requires the Client to provide any account access MySiteCare needs to configure a given third-party service.",
    ],
  },
];

export const PLAN_SLUG_TO_NAME = {
  ownership: "Website Ownership (One-Time)",
  "website-ownership": "Website Ownership (One-Time)",
  assisted_setup_launch: "Assisted Setup & Launch (One-Time)",
  "assisted-setup-launch": "Assisted Setup & Launch (One-Time)",
  maintenance: "Maintenance Only",
  "maintenance-only": "Maintenance Only",
  responsibility: "Responsibility Plan",
  "responsibility-plan": "Responsibility Plan",
  priority: "Priority & Reputation Care",
  "priority-reputation-care": "Priority & Reputation Care",
};

export function getPlanBySlug(slug) {
  // Assisted Setup & Launch lives in PLAN_ADD_ONS (a Website Ownership sub-option),
  // not PLANS — checked as a fallback so direct links/lookups by its slug still resolve.
  return PLANS.find((p) => p.slug === slug || p.id === slug) || PLAN_ADD_ONS.find((p) => p.slug === slug || p.id === slug);
}

export function getRegionalPlan(plan, region) {
  const regional = plan?.regional?.[region] || {};
  return {
    ...plan,
    ...regional,
    included: regional.included || plan.included,
    excluded: regional.excluded || plan.excluded,
    notes: [...(plan.notes || []), ...(regional.notes || [])],
  };
}

export const FAQS = [
  {
    q: "What am I actually buying with MySiteCare?",
    a: "You choose a clearly defined responsibility model — not marketing promises. You can own the website and manage it yourself, buy basic maintenance while keeping responsibility for problems, or delegate covered technical responsibility. The selected plan and accepted scope decide who handles each issue.",
  },
  {
    q: "What's the difference between Ownership and Responsibility?",
    a: "Ownership means you receive the website and you manage updates, security, backups, and fixes. Responsibility means the website exists as part of an ongoing service — and we handle maintenance, monitoring, and plan-covered fixes. Monthly payments do not automatically create ownership rights.",
  },
  {
    // Deliberately no price digits here — see the current, region-correct price
    // FAQ.js always substitutes for this exact question via getPlanPrice()/
    // getCombinedOwnershipTotal(), so this text can never go stale like the old
    // "€900 / ₱64,000" wording did once ownership and setup were unbundled. This
    // fallback only renders if that override ever fails to match.
    q: "What is included in Website Ownership?",
    a: "Website Ownership is a one-time purchase — see current pricing on the Plans page. It includes the agreed standalone website package, a full data export of every business module you've enabled, and setup documentation. Deployment is not included; Assisted Setup & Launch is a separate, optional one-time add-on if you'd like MySiteCare to handle it.",
  },
  {
    q: "Can my storefront accept online payments?",
    a: "MySiteCare's own billing (your MySiteCare invoices) accepts Bank Transfer, GCash, or PayMongo. For your own website's storefront, checkout currently supports Cash on Delivery, Pay on Pickup, and Bank Transfer / Manual Payment — it never marks an order as paid until a real payment is confirmed. If you have your own PayMongo merchant account and want online checkout, Assisted Setup & Launch can help you connect it. MySiteCare's own PayMongo account and credentials are never used for your customers' purchases, and a handover package never contains them.",
  },
  {
    q: "What happens if I choose Website Ownership?",
    a: "You receive the complete standalone website package and setup documentation in full — nothing is held back. Deployment, hosting setup, and domain configuration become your own responsibility (or your developer's) from there.",
  },
  {
    q: "Can my own developer set it up?",
    a: "Yes. That's one of the main purposes of the Website Ownership package — it's a complete, documented handover specifically so you or a developer of your choice can deploy it.",
  },
  {
    q: "Can MySiteCare set it up later?",
    a: "Yes. Assisted Setup & Launch can be purchased at the same time as Website Ownership, or added on afterward whenever you're ready — you never need to repurchase ownership to add it.",
  },
  {
    q: "Do you guarantee leads, traffic, or SEO rankings?",
    a: "No. We don't sell marketing outcomes. We do not guarantee leads, traffic, rankings, conversions, or business results. Your website is infrastructure. Outcomes depend on pricing, service quality, market, competition, and operations.",
  },
  {
    q: "What's included in my plan — and what's not?",
    a: "What's included is exactly what's written in your plan scope (hosting, monitoring, backups, updates, and eligible edits where stated). Anything not written is not included — including redesigns, new features, copywriting, marketing, and complex integrations. If it's not written and agreed, it's not included.",
  },
  {
    q: "Do you offer emergency support and what counts as \u201Curgent\u201D?",
    a: "Urgent means the website is down, broken, hacked, or payment/checkout is failing (if you have e-commerce). \u201CI want a change today\u201D is not urgent. Where emergency support is included, we prioritize outages first.",
  },
  {
    q: "What counts as \u201Csmall edits\u201D?",
    a: "Small edits are minor, non-structural changes like updating text, replacing images you provide, or changing hours, contact details, or service lists. They do not include redesigns, new features, major layout changes, new pages, or integrations. Included edits are time-based (e.g., up to 30 minutes/month on eligible plans). Time doesn't roll over.",
  },
  {
    q: "Can you do unlimited edits if I pay monthly?",
    a: "No. Unlimited edits create unlimited responsibility. Your plan includes a defined amount of edit time where stated. Anything beyond that is quoted separately or scheduled for a future month.",
  },
  {
    q: "How fast do you handle edit requests?",
    a: "We handle edits in a queue, prioritized by severity (site down beats cosmetic changes). Typical small edits are handled within a reasonable timeframe depending on workload and your plan. If you need guaranteed turnaround times, that must be explicitly agreed in writing.",
  },
  {
    q: "Can I update the site myself?",
    a: "Yes — if you own the website and have admin access. If your changes break the site, fixes may be billable or out of plan scope. Rule: if you change it, you own the risk.",
  },
  {
    q: "Do you guarantee my website will never get hacked?",
    a: "No provider can guarantee that. Where included, we apply best-practice security hardening, updates, backups, and monitoring to reduce risk. But 100% security is not guaranteed.",
  },
  {
    q: "What if I add plugins or tools myself?",
    a: "If you add third-party plugins, scripts, or integrations, you also add risk. Issues caused by third-party changes may be outside plan scope. Rule: if we didn't add it, we don't automatically support it.",
  },
  {
    q: "Do you manage backups and can you restore my site?",
    a: "Where included, we run backups and can restore to a recent backup point. Restores are for disasters (hack, corruption, major break) — not as a way to undo frequent client changes. No backup system can promise zero data loss between backup points.",
  },
  {
    q: "Who is responsible for passwords and account access?",
    a: "You are responsible for keeping your passwords safe, using strong passwords, and not sharing access unnecessarily. If compromised access causes damage, recovery may be out of plan scope.",
  },
  {
    q: "What happens if I stop paying?",
    a: "Services are active only while payment is current. If payment stops or fails, maintenance/monitoring/support may stop and hosting may be suspended. The website may go offline. We are not responsible for downtime, data loss, or business impact caused by non-payment.",
  },
  {
    q: "If I'm on a care plan, do I own the website?",
    a: "Not by default. Care plans are a responsibility relationship, not a website sale. Ownership exists only when purchased explicitly under the ownership option (or a separate transfer agreement).",
  },
  {
    q: "Do you offer refunds?",
    a: "Generally, no — because time, setup, and service capacity are reserved for you. If a refund exception exists, it must be in writing. See Terms & Conditions for the formal policy.",
  },
  {
    q: "What happens if an invoice is late or a payment fails?",
    a: "If payment fails, service can pause until it's fixed. Repeated late payments may trigger suspension or require prepayment going forward.",
  },
  {
    q: "What are preview websites and do I get to keep them?",
    a: "Previews are private, temporary demonstration sites for evaluation. They can expire or be removed and do not include hosting or ongoing service. You keep nothing unless you choose a paid option.",
  },
  {
    q: "If I buy ownership, what do I receive?",
    a: "You receive the website deliverables and agreed access/credentials needed to run it. After transfer, ongoing maintenance and fixes are your responsibility unless you purchase a care plan.",
  },
  {
    q: "Can you transfer my site to another provider?",
    a: "Yes — if you own the website (or have a written transfer agreement). Transfers are scheduled and may be billable depending on complexity and plan scope.",
  },
  {
    q: "What happens if the website goes down?",
    a: "If your plan includes monitoring, we investigate and fix plan-covered causes (hosting issues, updates, core breakages). Downtime caused by non-payment, third-party services, or client changes may be outside scope. \u201CUptime\u201D depends on hosting, DNS, plugins, and third parties. We reduce risk — we don't control the internet.",
  },
  {
    q: "Do you write content, take photos, or translate text?",
    a: "Not by default. You provide text and images unless content production is explicitly included and priced.",
  },
  {
    q: "Are third-party costs included (domain, email, paid plugins, ads)?",
    a: "Usually not. Domains, email services, premium plugins/themes, payment gateways, and ad spend are third-party costs unless explicitly included. If a third party changes pricing or terms, that's on them — not us.",
  },
  {
    q: "Do you do ads, SEO, or social media marketing?",
    a: "No. MySiteCare is not a marketing agency. We focus on operational responsibility for the website itself.",
  },
  {
    q: "How do I get started?",
    a: "Use the contact form or email us. We'll confirm what you have, what you need, and which option fits. We keep scope clean. If it's not written and agreed, it's not included.",
  },
];
